import {z} from 'zod'

import {AddressModel} from './address.js'
import {Price} from './price.js'
import {conditionGroupsModel} from './conditions.js'
import {PARCEL_CONDITION_VARIABLES} from '../functions/parcels/parcelConditions.js'

const ParcelProductsModel = z.enum(['Aangetekend', 'Aangetekende brief', 'Afhaalpunt', 'Antwoordnummer', 'Adreslabel', 'Avondlevering', 'Brievenbuspakket', 'Brievenbuspakket 48 uurs', 'Brievenbuspakket zonder Track & Trace', 'Envelop', 'Handtekening voor ontvangst', 'Leeftijdscheck 18+', 'Niet bij buren', 'Pick & Ship', 'Pick & Return', 'Postbus', 'Px', 'Retour', 'Retour brievenbuspakket', 'Retour bij niet thuis', 'Same Day', 'Spoed levering', 'Standaard zending', 'Verificatiecode', 'Verspakket', 'Verzekerd', 'Zondaglevering', 'Zonder Track & Trace'])

const PickupPointModel = z.object({
    id: z.string(),
    carrier: z.string(),
    carrierDisplayName: z.string().optional(),
    description: z.string(),
    distance: z.number(),
    address: z.object({
        name: z.string(),
        street: z.string(),
        nr: z.string(),
        addition: z.string(),
        postalCode: z.string(),
        city: z.string(),
        country: z.string()
    }),
    openingHours: z.object({
        monday: z.string().optional(),
        tuesday: z.string().optional(),
        wednesday: z.string().optional(),
        thursday: z.string().optional(),
        friday: z.string().optional(),
        saturday: z.string().optional(),
        sunday: z.string().optional()
    }).optional(),
    location: z.object({
        lat: z.number(),
        lng: z.number()
    }).optional()
})

const ParcelPrice = z.string().or(z.object({
    min: z.string(),
    max: z.string()
}))

const ParcelFeeModel = z.object({
    id: z.string().optional(), // TODO this should be required but for compatibility reasons it is optional for now
    price: ParcelPrice,
    percentage: z.string().optional(),
    basedOn: z.enum(['fixedPrice', 'percentageOfProduct', 'percentageOfProductPlusFixedPriceFees']).optional(),
    quantity: z.number().optional(),
    automaticallyAdded: z.boolean().optional(),
    description: z.string(),
    productCode: z.string()
})

const NewParcelModel = z.object({
    date: z.string().optional(),
    customer: z.string(),
    reseller: z.string().optional(),
    branchId: z.string().max(100).optional(),
    resellerId: z.string().optional(),
    createdByCustomer: z.boolean().optional(),
    createdBy: z.string().optional(),
    contact: z.string().optional(),
    senderAddress: AddressModel,
    address: AddressModel,
    pickupDate: z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/).optional(),
    pickupAddress: AddressModel.optional(),
    carrier: z.string(),
    options: z.array(z.string()),
    emailTT: z.string().optional(),
    reference: z.string().optional(),
    comment: z.string().optional(),
    weight: z.number(),
    width: z.number().optional(),
    height: z.number().optional(),
    length: z.number().optional(),
    price: Price.or(z.literal('priceScale')),
    priceScaleRange: z.object({
        min: z.string(),
        max: z.string()
    }).optional(),
    priceCategoryId: z.string().optional(),
    // The price scale the parcel was invoiced at and its price category, so a recalculation after invoicing keeps that
    // scale while the parcel stays in that category
    priceScaleIndex: z.number().optional(),
    priceScaleCategoryId: z.string().optional(),
    priceExplanation: z.object({
        description: z.string().optional(),
        product: z.object({
            name: z.string().optional(),
            countryGroup: z.string().optional(),
            countryGroupIndex: z.number().optional(),
            category: z.string().optional(),
            categoryIndex: z.number().optional(),
            price: ParcelPrice.optional()
        }).optional(),
        options: z.array(z.object({
            name: z.string(),
            price: ParcelPrice.optional(),
            percentage: z.string().optional()
        })).optional(),
        fees: z.array(z.object({
            name: z.string().optional(),
            price: ParcelPrice.optional(),
            quantity: z.number().optional(),
            percentage: z.string().optional()
        })).optional()
    }).optional(),
    productCodes: z.array(z.object({
        description: z.string().optional(),
        productCode: z.string().optional(),
        quantity: z.number()
    })),
    fees: z.array(ParcelFeeModel),
    notes: z.string().optional(),
    history: z.array(z.object({
        type: z.enum(['resellerOnly', 'invoice']).optional(),
        time: z.string().optional(),
        timestamp: z.string(),
        description: z.string().optional(),
        who: z.string(),
        status: z.number().optional(),
        eventTime: z.string().optional(),
        externalStatus: z.union([z.number(), z.string()]).optional().nullable(),
        invoiceId: z.string().optional(),
        invoiceNumber: z.string().optional(),
        invoicedAmount: z.string().optional()
    })),
    content: z.array(z.object({
        orderLineId: z.string().optional(),
        itemId: z.string(),
        isSerialItem: z.boolean().optional(),
        isBatchItem: z.number().optional(),
        WarehouseCode: z.string().optional(),
        quantity: z.number(),
        description: z.string(),
        weight: z.number(),
        value: z.string(),
        price: z.string().optional(),
        taxAmount: z.string().optional(),
        hsCode: z.string().optional(),
        countryOrigin: z.string().optional(),
        comment: z.string().optional()
    })).optional(),
    customs: z.object({
        typeItem: z.enum(['Commercial Goods', 'Returned Goods', 'Commercial Sample', 'Documents', 'Gift', '']),
        typeNumber: z.string(),
        number: z.string(),
        // ISO 4217, missing means EUR. Integrations may store codes outside customsCurrencies
        currency: z.string().optional(),
        content: z.array(z.object({
            description: z.string(),
            quantity: z.number(),
            weight: z.number(),
            value: z.string(),
            hsCode: z.string(),
            countryOrigin: z.string(),
            comment: z.string().optional(),
            // Field for return parcels
            return: z.boolean().optional(),
            returnQuantity: z.number().optional(),
            returnReason: z.string().optional(),
            returnReasonComment: z.string().optional()
        }))
    }).optional(),
    plugin: z.string().optional(),
    timeConfirmed: z.string().optional(),
    externId: z.string().optional(),
    webshop: z.string().optional(),
    webshopId: z.string().optional(),
    phone: z.string().or(z.literal('')).optional(),
    deliveryTime: z.string().optional(),
    cover: z.number().min(0).max(9999).optional(),
    pickupPoint: PickupPointModel.optional(),
    verificationCode: z.string().optional(),

    priceLocked: z.boolean().optional(),
    invoiceDate: z.string().optional(),
    // The invoice date just before the parcel was first invoiced, it gets this date back once all its invoices are removed
    invoiceDateBeforeInvoice: z.string().optional(),
    invoiced: z.boolean().optional(),
    invoicedAmount: z.string().optional(),
    status: z.number().optional(),
    pxBarcode: z.string().max(100).or(z.literal('')).optional(),
    barcode: z.string().optional(),
    trackTrace: z.string().optional(),
    trackTraceLink: z.string().optional(),
    label: z.string().optional(),
    labelId: z.string().optional(),
    labelZpl: z.string().optional(),
    labelZplId: z.string().optional(),
    webhookURL: z.string().optional(),
    additionalProps: z.record(z.any()).optional(),
    confirmedEmailSent: z.boolean().optional(),
    shippedEmailSent: z.boolean().optional(),
    deliveredEmailSent: z.boolean().optional(),
    retourId: z.string().max(100).optional(),
    refundMethod: z.string().max(200).optional(),
    refundMethodComment: z.string().max(2000).optional(),
    molliePaymentId: z.string().optional(),
    paymentStatus: z.string().optional(),
    printLockedAt: z.string().optional(),
    retourCreationLockedAt: z.string().optional()
})

const ParcelModel = NewParcelModel.extend({
    _id: z.string()
})

const ParcelConditionGroupsModel = conditionGroupsModel(PARCEL_CONDITION_VARIABLES)

export {ParcelModel, NewParcelModel, ParcelProductsModel, ParcelFeeModel, PickupPointModel, ParcelPrice, ParcelConditionGroupsModel}
